import {customElement, property, state} from 'lit/decorators.js';
import {html, LitElement, nothing, PropertyValues} from "lit";
import {selectStyles} from "@/components/select/select.styles";
import {labelStyles} from "@/components/label/label.styles";
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {SbkLabelMixin} from "@/components/label/label-mixin";
import {when} from 'lit/directives/when.js';
import {Validator, RequiredValidator} from "@/components/validators/validators";
import {query, queryAll} from "lit/decorators.js";
import {SbkSelectOption} from "@/components/select-option/select-option";
import {SbkInput} from "@/components/input/input";
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import "@/components/select-option-tag/select-option-tag";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {classMap,ClassInfo} from "lit/directives/class-map.js";

import './select.scss';

// eslint-disable-next-line
declare const TYPO3: any;

type SelectOption = {
    value: string;
    label: string;
    description?: string;
    selected?: boolean;
};


@customElement('sbk-select')
export class SbkSelect extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    static formAssociated = true;

    static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

    _internals: ElementInternals;

    activeIndex: number = 0;

    @state()
    selectedOption!: SelectOption;

    @state()
    selectedOptions: SelectOption[] = [];

    @property({type: Array})
    options: SelectOption[] = [];

    @property({type: Boolean})
    multiple: boolean = false;

    @queryAll('sbk-select-option')
    selectOptions!: SbkSelectOption[];

    @queryAll('sbk-select-option:not([hidden])')
    _visibleSelectOptions!: Array<SbkSelectOption>;

    @query('#select')
    _selectElement!: HTMLDivElement;

    @query('#selectMenuWrapper')
    _selectMenuWrapperElement!: HTMLDivElement;

    @query('#selectSearchWrapper')
    _selectSearchWrapperElement!: HTMLDivElement;

    @query('#listbox')
    _listboxElement!: HTMLDivElement;

    @query('#search')
    _searchElement!: SbkInput;

    @state()
    private overflowCount: number = 0;

    @query('.select__text-wrapper')
    private textWrapper!: HTMLElement;

    @property({type: Boolean, reflect: true})
    disabled: boolean = false

    @property()
    description: string = ''

    @property()
    menuDirection: string = ''

    @property({type: Boolean, attribute: 'enable-search'})
    enableSearch: boolean = false

    @property({reflect: true})
    name: string = ''

    @property({type: Boolean})
    readonly: boolean = false

    @property({type: Boolean})
    required: boolean = false

    @property({type: Boolean})
    sort: boolean = false

    @state()
    visible: boolean = true

    @state()
    open: boolean = false

    @state()
    noFilterResult: boolean = false

    @property({ type: Boolean })
    sortByName?: boolean = false

    private _resizeHandler: () => void;

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': RequiredValidator,
    }

    selectActions = {
        Close: 0,
        CloseSelect: 1,
        First: 2,
        Last: 3,
        Next: 4,
        Open: 5,
        PageDown: 6,
        PageUp: 7,
        Previous: 8,
        Select: 9,
        Type: 10,
    };


    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });

        // Resize event for overflow Clip
        this._resizeHandler = this._updateOverflowChip.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this._resizeHandler);
        this._updateOverflowChip();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this._resizeHandler);
    }

    static get styles() {
        return [
            labelStyles,
            selectStyles
        ];
    }

    render() {
        return html`
            <div @selectItem=${this._updateValue} class="input__wrapper${this.invalid ? ' input__wrapper--error' : ''}${this.disabled || this.readonly ? ' input__wrapper--disabled' : ''}${this.open ? ' select--open' : ''}">
                <div id="selectMenuWrapper" class="select-menu__wrapper">
                    ${this.labelTemplate(`${this.name}-label`, true, this.invalid)}
                    ${when(this.description,
                        () => html`
                            <sbk-annotation id="annotation" variant="info" ?disabled=${this.disabled || this.readonly}>${unsafeHTML(this.description)}</sbk-annotation>
                        `)}
                    <div aria-controls="listbox"
                         aria-expanded=${this.open}
                         aria-haspopup="listbox"
                         aria-labelledby="${this.name}-label"
                         aria-describedby=${this.description || this.invalid ? 'annotation' : nothing}
                         id="select"
                         class=${classMap(this.getSelectInputClassMap())}
                         ?disabled=${this.disabled}
                         ?readonly=${this.readonly}
                         @click=${this._toggleSelect}
                         @blur=${this._onBlur}
                         @keydown=${this._onKeyDown}
                         role="combobox"
                         tabindex="0">
                        <div class="select__text-wrapper${this.selectedOptions.length || this.selectedOption?.value ? '' : ' select__text-wrapper--empty-option'}">
                            ${this._renderSelectedOption()}
                        </div>
                        ${this.overflowCount > 0 ? html`
                            <sbk-badge>+${this.overflowCount}</sbk-badge>
                        ` : nothing}
                        <div class="select__icon-wrapper">
                            <sbk-icon size="24" symbol="chevron-down"></sbk-icon>
                        </div>
                        <div class="select-menu ${this.menuDirection}" @focusout=${this._onBlur}>
                            ${this._renderSearchInput()}
                            <div class="select__listbox"
                                role="listbox"
                                id="listbox" 
                                 aria-labelledby="${this.name}-label"
                                tabindex="-1"
                                aria-multiselectable=${this.multiple}>
                                ${this.options
                                        .slice()  // Create a shallow copy of this.options
                                        .sort((a, b) => this.sortByName ? a.label.localeCompare(b.label) : 0)
                                        .map((selectOption: SelectOption, index: number) => html`
                                            <sbk-select-option role="option"
                                                               id="${this.id}-${index}"
                                                               index=${index}
                                                               value=${selectOption.value}
                                                               description=${selectOption.description}
                                                               ?selected=${selectOption.selected}
                                                               ?multiple=${this.multiple}
                                                               label=${selectOption.label}>
                                                ${selectOption.label}
                                            </sbk-select-option>
                                        `)}
                                <p class="select-no-option" ?hidden=${!this.noFilterResult}>${this._getTYPO3Label('noEntriesFound', 'Keine Einträge gefunden')}</p>
                            </div>
                        </div>
                    </div>
                </div>
                ${this.invalid ? this.validationErrorTemplate(`${this.name}-annotation`) : ""}
            </div>
        `;
    }

    _renderSearchInput() {
        if (!this.enableSearch) {
            return '';
        }
        return html`
            <div id="selectSearchWrapper" class="select__search-wrapper">
                <sbk-input @input-input=${this._filter} @keydown=${this._onKeyDown} id="search" name="search" placeholder=${this._getTYPO3Label('search', 'Suchen')} type="search" autocomplete="off" aria-label="search"
                           size="small"></sbk-input>
            </div>
        `;
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('options')) {

            this.selectedOptions = [];

            this.options.forEach((selectOption, index) => {
                if (selectOption.selected) {
                    this.selectedOptions.push(selectOption);
                    this.activeIndex = index;
                    this.selectedOption = selectOption;
                }
            });

            if (!this.multiple && this.selectedOptions.length === 0 && this.options.length > 0) {
                this.activeIndex = 0;
                this.selectedOption = this.options[this.activeIndex];
            }

            this._internals.setFormValue(this.multiple
                ? this.selectedOptions.map(option => option.value).join(',')
                : this.selectedOption.value);

            const selectValueChangedEvent = new CustomEvent('select-value-changed', {
                detail: {
                    value: this.multiple
                        ? this.selectedOptions.map(option => option.value).join(',')
                        : this.selectedOption.value
                },
                bubbles: true
            });
            this.dispatchEvent(selectValueChangedEvent);
        }
    }


    updated(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('open')) {
            if (this.open && this.enableSearch) {
                this._searchElement.focus();
            }
        }
        if (changedProperties.has('options')) {
          this.requestUpdate();
        }

    }

    _getTYPO3Label(key: string, defaultLabel: string): string {
        return typeof TYPO3 !== "undefined" ? TYPO3.lang[key] : defaultLabel;
    }

    _filter(event: CustomEvent) {
        this.selectOptions.forEach(selectOption => {
            const label = selectOption.getAttribute("label");
            const labelText = label ? label.toLowerCase() : "";

            selectOption.style.display = !labelText.includes(event.detail.value.toLowerCase()) ? 'none' : 'block';
        });
        this._modifyHeight();
        this.noFilterResult = !this._visibleSelectOptions.length;
        this.activeIndex = 0;
    }

    _updateValue(event: CustomEvent) {
        if (this.options?.length) {
            if (this.multiple) {
                // Toggle the selected state of the option in multiselect mode
                this.options.forEach((selectOption) => {
                    if (selectOption.value === event.detail.value) {
                        selectOption.selected = !selectOption.selected;
                        if (selectOption.selected) {
                            this.selectedOptions.push(selectOption);
                        } else {
                            this.selectedOptions = this.selectedOptions.filter(opt => opt.value !== selectOption.value);
                        }
                    }
                });
            } else {
                // Single select mode
                this.options.forEach((selectOption, index) => {
                    if (selectOption.value === event.detail.value) {
                        selectOption.selected = true;
                        this.activeIndex = index;
                        this.selectedOption = selectOption;
                    } else {
                        selectOption.selected = false;
                    }
                });
            }
            this.updateValue();
        }
    }

    updateValue() {
        this._updateOverflowChip();

        const validators = this.getValidators();
        const value = this.multiple
            ? this.selectedOptions.map(option => option.value).join(',') // For multiselect, join selected values
            : this.selectedOption.value;

        this.handleErrors(validators, value);
        this._internals.setFormValue(value);

        // Dispatch value changed event
        const selectValueChangedEvent = new CustomEvent('select-value-changed', {
            detail: { value },
            bubbles: true,
        });
        this.dispatchEvent(selectValueChangedEvent);
    }

    _toggleSelect() {
        this._updateMenuState(!this.open);
    }

    _updateMenuState(state: boolean, callFocus = true) {
        if (this.open === state) {
            return;
        }

        this.open = state;

        if (this.open) {

            this._modifyHeight();
            const rect = this.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const menu = this.shadowRoot?.querySelector('.select-menu') as HTMLElement;
            const dropdownHeight = this.offsetHeight + menu.offsetHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                this.menuDirection = 'up';
            } else {
                this.menuDirection = 'down';
            }
        }

        if (!this.open) {
            this.selectOptions.forEach((optionEl) => {
                optionEl.classList.remove('select-option--current');
            });
        }

        // update activedescendant
        const activeID = state ? `${this.id}-${this.activeIndex}` : '';
        this._selectElement.setAttribute('aria-activedescendant', activeID);

        if (activeID === '' && !this._isElementInView(this._selectElement)) {
            this._selectElement.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        }

        // move focus back to the combobox, if needed
        callFocus && this._selectElement.focus();
    }

    _modifyHeight() {
        let height = 0;
        Array.from(this.selectOptions).some(function(option) {
            if (height + option.offsetHeight > 300) {
                height += 26;
                return true;
            }
            height += option.offsetHeight;
            return false;
        });
        this._listboxElement.style.height = `${height}px`;
        this._listboxElement.style.maxHeight = `${height}px`;
    }

    _onKeyDown(event: KeyboardEvent) {
        const max = this._visibleSelectOptions.length - 1;

        const action = this._getActionFromKey(event, this.open);

        switch (action) {
            case this.selectActions.Last:
            case this.selectActions.First:
                return this._updateMenuState(true);
            // intentional fallthrough
            case this.selectActions.Next:
            case this.selectActions.Previous:
            case this.selectActions.PageUp:
            case this.selectActions.PageDown:
                event.preventDefault();
                return this._onOptionChange(
                    this._getUpdatedIndex(this.activeIndex, max, action)
                );
            case this.selectActions.CloseSelect:
                event.preventDefault();
                this._selectOption(this.activeIndex);
                return this._updateMenuState(false);
            // intentional fallthrough
            case this.selectActions.Close:
                event.preventDefault();
                return this._updateMenuState(false);
            case this.selectActions.Open:
                event.preventDefault();
                this._visibleSelectOptions[this.activeIndex].classList.add('select-option--current');
                return this._updateMenuState(true);
        }
    }

    _selectOption(index: number) {
        this.activeIndex = index;
        const selectedOption = this.options[this._visibleSelectOptions[index].index];

        if (this.multiple) {
            selectedOption.selected = !selectedOption.selected;
            if (selectedOption.selected) {
                this.selectedOptions.push(selectedOption);
            } else {
                this.selectedOptions = this.selectedOptions.filter(opt => opt.value !== selectedOption.value);
            }
        } else {
            // Single select logic
            this.options.forEach((option, idx) => {
                option.selected = idx === index;
            });
            this.selectedOption = selectedOption;
        }

        this.updateValue();
    }


    _renderSelectedOption() {
        if (this.multiple && this.selectedOptions.length > 0) {
            return html`
                ${this.selectedOptions.map(opt => html`
                    <sbk-select-option-tag
                        .label=${opt.label}
                        .value=${opt.value}
                        @remove-option=${this._handleRemoveOption}
                    ></sbk-select-option-tag>
                `)}
            `;
        } else if (!this.multiple && this.selectedOption) {
            return this.selectedOption.label || this._getTYPO3Label('selectPlaceholder', 'Select option');
        }

        //Show placeholder if nothing is selected
        return this._getTYPO3Label('selectPlaceholder', 'Select option');
    }

    _handleRemoveOption(event: CustomEvent) {
        const valueToRemove = event.detail.value;
        this.selectedOptions = this.selectedOptions.filter(opt => opt.value !== valueToRemove);
        this.options = this.options.map(opt => {
            if (opt.value === valueToRemove) {
                opt.selected = false;
            }
            return opt;
        });
        this.updateValue();
    }

    _getActionFromKey(event: KeyboardEvent, menuOpen: boolean) {
        const {key, altKey, ctrlKey, metaKey} = event;
        const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
        // handle opening when closed
        if (!menuOpen && openKeys.includes(key)) {
            return this.selectActions.Open;
        }

        // home and end move the selected option when open or closed
        if (key === 'Home') {
            return this.selectActions.First;
        }
        if (key === 'End') {
            return this.selectActions.Last;
        }

        // handle typing characters when open or closed
        if (
            key === 'Backspace' ||
            key === 'Clear' ||
            (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
        ) {
            return this.selectActions.Type;
        }

        // handle keys when open
        if (menuOpen) {
            if (key === 'ArrowUp' && altKey) {
                return this.selectActions.CloseSelect;
            } else if (key === 'ArrowDown' && !altKey) {
                return this.selectActions.Next;
            } else if (key === 'ArrowUp') {
                return this.selectActions.Previous;
            } else if (key === 'PageUp') {
                return this.selectActions.PageUp;
            } else if (key === 'PageDown') {
                return this.selectActions.PageDown;
            } else if (key === 'Escape') {
                return this.selectActions.Close;
            } else if (key === 'Enter' || key === ' ') {
                return this.selectActions.CloseSelect;
            }
        }
    }

    _getUpdatedIndex(currentIndex: number, maxIndex: number, action: number) {
        const pageSize = 10; // used for pageup/pagedown

        switch (action) {
            case this.selectActions.First:
                return 0;
            case this.selectActions.Last:
                return maxIndex;
            case this.selectActions.Previous:
                return Math.max(0, currentIndex - 1);
            case this.selectActions.Next:
                return Math.min(maxIndex, currentIndex + 1);
            case this.selectActions.PageUp:
                return Math.max(0, currentIndex - pageSize);
            case this.selectActions.PageDown:
                return Math.min(maxIndex, currentIndex + pageSize);
            default:
                return currentIndex;
        }
    }

    _onOptionChange(index: number) {
        // update state
        this.activeIndex = index;

        // update aria-activedescendant
        this._selectElement.setAttribute('aria-activedescendant', `${this.id}-${index}`);

        // update active option styles
        const options = this._visibleSelectOptions;
        [...options].forEach((optionEl) => {
            optionEl.classList.remove('select-option--current');
        });
        options[index].classList.add('select-option--current');

        // ensure the new option is in view
        if (this._isScrollable(this._listboxElement)) {
            this._maintainScrollVisibility(options[index], this._listboxElement);
        }

        // ensure the new option is visible on screen
        // ensure the new option is in view
        if (!this._isElementInView(options[index])) {
            options[index].scrollIntoView({behavior: 'smooth', block: 'nearest'});
        }

    }

    // check if element is visible in browser view port
    _isElementInView(element: HTMLElement) {
        const bounding = element.getBoundingClientRect();
        return (
            bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
            bounding.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    // check if an element is currently scrollable
    _isScrollable(element: HTMLDivElement) {
        return element && element.clientHeight < element.scrollHeight;
    }

    // ensure a given child element is within the parent's visible scroll area
    // if the child is not visible, scroll the parent
    _maintainScrollVisibility(activeElement: SbkSelectOption, scrollParent: HTMLDivElement) {
        const parentRect = scrollParent.getBoundingClientRect();
        const activeRect = activeElement.getBoundingClientRect();

        if (activeRect.top < parentRect.top) {
            // Scroll up to show the element
            scrollParent.scrollTop -= (parentRect.top - activeRect.top);
        } else if (activeRect.bottom > parentRect.bottom) {
            // Scroll down to show the element
            scrollParent.scrollTop += (activeRect.bottom - parentRect.bottom);
        }
    }


    _onBlur(event: Event) {
        // do nothing if relatedTarget is contained within listboxEl
        if (event instanceof FocusEvent) {
            if (this._listboxElement.contains((<Element>event.relatedTarget))) {
                return;
            }
            if (Array.from(this.selectOptions).find(o => o.id === (<Element>event.relatedTarget)?.id)) {
                return;
            }
            if (this._searchElement && (<Element>event.relatedTarget) === this._searchElement) {
                return;
            }
        }

        if (this.open) {
            this._updateMenuState(false, false);
        }
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this.selectedOption = this.options[0];
        this._internals.setFormValue('');
    }

    setValue(): void {
        this._internals.setFormValue(this.selectedOption ? this.selectedOption.value : null);
    }

    async _updateOverflowChip() {
        if (!this.textWrapper || !this.multiple) return;

        // Dom updates have been applieed
        await this.updateComplete;
        requestAnimationFrame(() => {
            const containerWidth = this.textWrapper.offsetWidth - 28; // Get the width of the select text wrapper (visible area)
            const selectedOptions = Array.from(this.textWrapper.querySelectorAll('sbk-select-option-tag')) as HTMLElement[];

            let totalWidth = 0;
            let visibleCount = 0;

            for (const option of selectedOptions) {
                totalWidth += option.offsetWidth;
                if (totalWidth > containerWidth) {
                    break;
                }
                visibleCount++;
            }

            // Calulate overflow count
            const hiddenCount = this.selectedOptions.length - visibleCount;

            this.overflowCount = hiddenCount > 0 ? hiddenCount : 0;
        });
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }

    getSelectInputClassMap(): ClassInfo {
        return {
            'select-input': true,
            'select-input--multiple': this.multiple,
            'select-input--multiple-selected': this.multiple && !!this.selectedOptions.length,
        };
    }

    triggerFormControlDependencyChangeEvent() {
        const formControlDependencyChangeEvent = new CustomEvent('form-control-dependency-change', {
            bubbles: true
        });
        this.dispatchEvent(formControlDependencyChangeEvent);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "sbk-select": SbkSelect;
    }
}
